/**
 * Shareable-link utilities for TRITAL Care® skill checklists.
 *
 * Goal: generate a client-portable URL that encodes the clinician's aggregate
 * checklist payload into the URL fragment (#d=...). Because fragments never
 * reach the server, the recipient's browser reads the data locally.
 *
 * Not a cryptographic signature — this is a lightweight integrity + TTL
 * envelope. When a real backend ships, swap `encodeShare` for a token POST.
 */

const SCHEMA_VERSION = 1;
const DEFAULT_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/** UTF-8 safe base64url encoder (browser). */
function base64urlEncode(str) {
  const bytes = new TextEncoder().encode(str);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlDecode(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Cheap non-cryptographic integrity check — detects accidental URL corruption. */
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

/**
 * Encode a payload into a shareable URL path (e.g. /c#d=...).
 * @param {object} payload — e.g. the aggregate My Checklists JSON.
 * @param {object} [opts]
 * @param {number} [opts.ttlMs=14d] — time-to-live in ms.
 * @param {string} [opts.origin] — override origin (defaults to window.location.origin).
 * @returns {{url: string, expiresAt: string, sizeBytes: number}}
 */
export function encodeShare(payload, opts = {}) {
  const now = Date.now();
  const envelope = {
    v: SCHEMA_VERSION,
    iat: now,
    exp: now + (opts.ttlMs || DEFAULT_TTL_MS),
    payload,
  };
  const json = JSON.stringify(envelope);
  const b = base64urlEncode(json);
  const c = fnv1a(json);
  const origin = opts.origin || window.location.origin;
  const url = `${origin}/c#d=${b}&c=${c}`;
  return {
    url,
    expiresAt: new Date(envelope.exp).toISOString(),
    sizeBytes: url.length,
  };
}

/**
 * Decode a share fragment (from window.location.hash, sans leading '#').
 * @param {string} hash — e.g. 'd=...&c=...' or '#d=...&c=...'
 * @returns {{ok: boolean, reason?: string, payload?: object, meta?: object}}
 */
export function decodeShare(hash) {
  if (!hash) return { ok: false, reason: "missing" };
  const clean = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(clean);
  const d = params.get("d");
  const c = params.get("c");
  if (!d) return { ok: false, reason: "missing" };
  let json;
  try {
    json = base64urlDecode(d);
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (c && fnv1a(json) !== c) {
    return { ok: false, reason: "corrupted" };
  }
  let envelope;
  try {
    envelope = JSON.parse(json);
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (!envelope || envelope.v !== SCHEMA_VERSION) {
    return { ok: false, reason: "version" };
  }
  if (typeof envelope.exp === "number" && Date.now() > envelope.exp) {
    return {
      ok: false,
      reason: "expired",
      meta: { issuedAt: envelope.iat, expiresAt: envelope.exp },
    };
  }
  return {
    ok: true,
    payload: envelope.payload,
    meta: { issuedAt: envelope.iat, expiresAt: envelope.exp, version: envelope.v },
  };
}

export const SHARE_LINK_MAX_SAFE_BYTES = 6000;
export const QR_FIT_MAX_BYTES = 2300;
